import { uniq } from "es-toolkit/array";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";

/**
 * チャート系列として描画するフィールド定義。
 */
export type VizSeriesEntry = {
  key: string;
  label: string | null;
  color: string | null;
  metricKey: string | null;
  groupKey: string | null;
  groupValue: string | null;
  strokeDasharray: string | null;
};

export type VizSeriesEntryOverride = Partial<
  Pick<
    VizSeriesEntry,
    | "label"
    | "color"
    | "metricKey"
    | "groupKey"
    | "groupValue"
    | "strokeDasharray"
  >
>;

function buildExcludedKeys(dataset: VizDataset): Set<string> {
  const excluded = new Set<string>();
  const defaults = dataset.defaults;
  if (defaults?.category) excluded.add(defaults.category);
  if (defaults?.x) excluded.add(defaults.x);
  return excluded;
}

function collectUniqueKeys(
  keys: Iterable<string>,
  excluded: Set<string> = new Set<string>(),
): string[] {
  return uniq(
    Array.from(keys).filter((key) => key.length > 0 && !excluded.has(key)),
  );
}

function inferSeriesKeys(dataset: VizDataset): string[] {
  const defaults = dataset.defaults;
  const excluded = buildExcludedKeys(dataset);

  if (Array.isArray(defaults?.y) && defaults.y.length > 0) {
    return collectUniqueKeys(
      defaults.y.filter((key) => typeof key === "string"),
      excluded,
    );
  }
  if (defaults?.value) {
    return collectUniqueKeys([defaults.value], excluded);
  }

  const metricKeys = collectUniqueKeys(
    dataset.fields
      .filter((item) => item.role === "metric")
      .map((item) => item.name),
    excluded,
  );
  if (metricKeys.length > 0) return metricKeys;

  const numericKeys = collectUniqueKeys(
    dataset.fields
      .filter((item) => item.type === "number")
      .map((item) => item.name),
    excluded,
  );
  return numericKeys;
}

/**
 * 系列キー配列から描画用メタデータを組み立てる。
 */
export function buildSeriesEntries(
  dataset: VizDataset,
  keys: string[] = inferSeriesKeys(dataset),
  labelOverrides: Record<string, string> = {},
  entryOverrides: Record<string, VizSeriesEntryOverride> = {},
): VizSeriesEntry[] {
  const fieldMap = new Map(dataset.fields.map((field) => [field.name, field]));
  const uniqueKeys = collectUniqueKeys(keys);
  return uniqueKeys.map((key) => {
    const field = fieldMap.get(key);
    const override = entryOverrides[key] ?? {};
    const overrideLabel = labelOverrides[key];
    return {
      key,
      label:
        override.label ?? overrideLabel ?? field?.label ?? field?.name ?? null,
      color: override.color ?? null,
      metricKey: override.metricKey ?? key,
      groupKey: override.groupKey ?? null,
      groupValue: override.groupValue ?? null,
      strokeDasharray: override.strokeDasharray ?? null,
    };
  });
}
